import React from 'react';
import { Quote } from 'lucide-react';
import { CaseStudy } from '../types';
import '../styles/CaseStudies.css';

const CaseStudies: React.FC = () => {
  const caseStudies: CaseStudy[] = [
    {
      id: 1,
      client: 'SaaS onboarding automation',
      industry: 'B2B SaaS',
      challenge: 'Manual customer onboarding was taking two weeks per client, bottlenecked on CS and implementation engineers.',
      solution: 'Agentic pipeline that ingests the signed MSA, provisions the workspace, routes data mapping, and schedules training—auto-escalating the one step that actually needs a human.',
      results: [
        { metric: '24h', label: 'Onboarding time' },
        { metric: '3×', label: 'Efficiency gain' },
        { metric: '50%', label: 'Cost reduction' },
      ],
      testimonial: 'What used to be a sprint is now a Slack thread. Our CS team got their week back.',
    },
    {
      id: 2,
      client: 'Editorial payroll operations',
      industry: 'Digital Publishing',
      challenge: 'Freelance payroll was swallowing 15 hours per month of editorial leadership time and arriving late every cycle.',
      solution: 'Automated pipeline syncing timesheets → contracts → payroll provider with real-time approvals and GL coding. Exceptions surface in a single Slack channel.',
      results: [
        { metric: '90%', label: 'Time saved' },
        { metric: '100%', label: 'Accuracy rate' },
        { metric: '15h', label: 'Freed per month' },
      ],
      testimonial: 'Payroll went from a weekend task to a morning coffee.',
    },
    {
      id: 3,
      client: 'Unified financial reporting',
      industry: 'Financial Services',
      challenge: 'Critical data lived across 15+ systems with no shared source of truth. Monthly close was spreadsheet-driven and error-prone.',
      solution: 'Consolidated warehouse, dbt semantic layer, and executive dashboards with predictive forecasting. Anomaly detection now flags issues weeks before close.',
      results: [
        { metric: '10×', label: 'Faster reporting' },
        { metric: '100%', label: 'Data accuracy' },
        { metric: '$2M', label: 'Annual savings' },
      ],
      testimonial: 'For the first time, we read the business instead of reports about the business.',
    },
  ];

  return (
    <section id="case-studies" className="case-studies section-padding">
      <div className="container">
        <div className="section-kicker reveal">
          <span className="eyebrow">Results</span>
          <h2>Outcomes we&rsquo;ve shipped.</h2>
          <p>
            A few recent engagements—each one a real problem, real metrics, and an agent
            still running in production today.
          </p>
        </div>

        <div className="case-studies-grid">
          {caseStudies.map((study: CaseStudy) => (
            <article key={study.id} className="case-study-card reveal">
              <header className="card-header">
                <span className="industry-badge">{study.industry}</span>
                <h3>{study.client}</h3>
              </header>

              <div className="case-section">
                <span className="case-section-label">Challenge</span>
                <p>{study.challenge}</p>
              </div>

              <div className="case-section">
                <span className="case-section-label">Solution</span>
                <p>{study.solution}</p>
              </div>

              <div className="metrics-grid">
                {study.results.map((result, index: number) => (
                  <div key={index} className="metric">
                    <span className="metric-number gradient-text">{result.metric}</span>
                    <span className="metric-label">{result.label}</span>
                  </div>
                ))}
              </div>

              {study.testimonial && (
                <blockquote className="testimonial">
                  <Quote size={14} className="quote-mark" />
                  <p>{study.testimonial}</p>
                </blockquote>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
